import { apiClient, type ApiResponse } from "./apiClient";
import type {
  EnhanceTaggingRow,
  ImpactAssessmentRow,
  ImpactStatus,
} from "../components/RegulationDetails/TaggingImpactAssessment/types";
import { formatConditionToNaturalLanguage } from "../pages/AiPlanPage/EnhancedTaggingRule/formatConditionNaturalLanguage";

const IMPACTED_STYLE = { bg: "#FEE2E2", color: "#991B1B" } as const;
const NO_IMPACT_STYLE = { bg: "#DCFCE7", color: "#166534" } as const;

function normalizeVersion(version: string): string {
  const t = version.trim();
  if (!t) return "";
  return t.startsWith("v") || t.startsWith("V") ? t.slice(1) : t;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function pickString(
  item: Record<string, unknown>,
  keys: string[],
): string {
  for (const key of keys) {
    const value = item[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return "";
}

function pickNestedName(value: unknown): string {
  const rec = asRecord(value);
  if (!rec) return "";
  return pickString(rec, [
    "display_name",
    "displayName",
    "name",
    "label",
    "value",
  ]);
}

/** Prefer API display label over technical concept / field name. */
function pickConceptDimensionLabel(
  item: Record<string, unknown>,
  fallback = "",
): string {
  return (
    pickString(item, [
      "concept_display_name",
      "conceptDisplayName",
      "dimension_display_name",
      "dimensionDisplayName",
      "display_name",
      "displayName",
      "dimension",
      "concept_name",
      "conceptName",
      "concept",
    ]) ||
    pickNestedName(item.concept) ||
    fallback
  );
}

function pickRawCondition(item: Record<string, unknown>): unknown {
  for (const key of [
    "matched_condition",
    "matchedCondition",
    "matching_condition",
    "matchingCondition",
    "condition",
    "query",
    "rule_condition",
    "ruleCondition",
  ]) {
    if (Object.hasOwn(item, key) && item[key] != null && item[key] !== "") {
      return item[key];
    }
  }
  return undefined;
}

/**
 * Convert a Query Builder condition (object or JSON string) into natural language.
 * Falls back to the original string when formatting is not possible.
 */
export function resolveMatchingConditionDisplay(raw: unknown): string {
  if (raw == null) return "–";

  if (typeof raw === "object") {
    const formatted = formatConditionToNaturalLanguage(raw);
    return formatted || "–";
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return "–";

    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      try {
        const parsed: unknown = JSON.parse(trimmed);
        const formatted = formatConditionToNaturalLanguage(parsed);
        if (formatted) return formatted;
      } catch {
        // Keep the original string when JSON parse fails.
      }
    }

    return trimmed;
  }

  return "–";
}

/** Prefer array payloads; also accept `{ items | tags | results | matched_rules }` wrappers. */
export function extractDetailItems(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  const root = asRecord(payload);
  if (!root) return [];

  const listKeys = [
    "matched_rules",
    "matchedRules",
    "items",
    "tags",
    "results",
    "auto_tags",
    "autoTags",
    "conditional_tags",
    "conditionalTags",
  ] as const;

  for (const key of listKeys) {
    const nested = root[key];
    if (Array.isArray(nested)) return nested;
  }

  const skipGroupedKeys = new Set<string>(listKeys);

  // Grouped map: { Department: [{ name, ... }], Market: [...] }
  const grouped: unknown[] = [];
  for (const [concept, values] of Object.entries(root)) {
    if (skipGroupedKeys.has(concept)) continue;
    if (!Array.isArray(values)) continue;
    for (const value of values) {
      const rec = asRecord(value);
      if (!rec) continue;
      grouped.push({
        ...rec,
        concept_name:
          pickString(rec, ["concept_name", "conceptName", "dimension", "concept"]) ||
          concept,
        concept_display_name:
          pickString(rec, ["concept_display_name", "conceptDisplayName"]) ||
          undefined,
        dimension: pickConceptDimensionLabel(rec, concept),
      });
    }
  }
  return grouped;
}

function displayText(value: string): string {
  const t = value.trim();
  return t || "–";
}

function resolveImpactStatus(item: Record<string, unknown>): {
  impactLabel: ImpactStatus;
  impactBg: string;
  impactColor: string;
  hasImpact: boolean;
} {
  const rawLabel = pickString(item, [
    "impact_label",
    "impactLabel",
    "impact",
    "impacted_level",
    "impactLevel",
    "direct_impact",
    "directImpact",
  ]).toLowerCase();

  if (
    rawLabel === "impacted" ||
    rawLabel === "yes" ||
    rawLabel === "high" ||
    rawLabel === "medium" ||
    rawLabel === "low"
  ) {
    return {
      impactLabel: "Impacted",
      impactBg: IMPACTED_STYLE.bg,
      impactColor: IMPACTED_STYLE.color,
      hasImpact: true,
    };
  }

  if (
    rawLabel === "no impact" ||
    rawLabel === "no_impact" ||
    rawLabel === "no" ||
    rawLabel === "none"
  ) {
    return {
      impactLabel: "No Impact",
      impactBg: NO_IMPACT_STYLE.bg,
      impactColor: NO_IMPACT_STYLE.color,
      hasImpact: false,
    };
  }

  if (typeof item.impacted === "boolean") {
    if (item.impacted) {
      return {
        impactLabel: "Impacted",
        impactBg: IMPACTED_STYLE.bg,
        impactColor: IMPACTED_STYLE.color,
        hasImpact: true,
      };
    }
    return {
      impactLabel: "No Impact",
      impactBg: NO_IMPACT_STYLE.bg,
      impactColor: NO_IMPACT_STYLE.color,
      hasImpact: false,
    };
  }

  return {
    impactLabel: "",
    impactBg: "",
    impactColor: "",
    hasImpact: false,
  };
}

function expandAutoTagItem(raw: unknown, index: number): ImpactAssessmentRow[] {
  const item = asRecord(raw);
  if (!item) return [];

  const nestedValues = item.values ?? item.tags ?? item.concept_values;
  if (Array.isArray(nestedValues) && nestedValues.length > 0) {
    const dimension = pickConceptDimensionLabel(item);
    const parentDisplayName = pickString(item, [
      "concept_display_name",
      "conceptDisplayName",
    ]);

    return nestedValues.flatMap((value, valueIndex) => {
      const valueRec = asRecord(value);
      if (!valueRec) return [];
      const merged: Record<string, unknown> = {
        ...valueRec,
        concept_display_name:
          pickString(valueRec, [
            "concept_display_name",
            "conceptDisplayName",
          ]) ||
          parentDisplayName ||
          undefined,
        concept_name:
          pickString(valueRec, ["concept_name", "conceptName"]) ||
          pickString(item, ["concept_name", "conceptName"]) ||
          dimension,
      };
      // Resolve label after inheriting parent concept_display_name.
      merged.dimension = pickConceptDimensionLabel(merged, dimension);
      return expandAutoTagItem(merged, index * 1000 + valueIndex);
    });
  }

  const dimension = pickConceptDimensionLabel(item);

  const tagValue =
    pickString(item, [
      "tag_value",
      "tagValue",
      "concept_value_name",
      "conceptValueName",
      "concept_value",
      "conceptValue",
      "name",
      "value",
      "label",
    ]) || pickNestedName(item.concept_value ?? item.conceptValue);

  if (!dimension && !tagValue) return [];

  const impact = resolveImpactStatus(item);
  const id =
    pickString(item, ["id", "uuid", "tag_id", "tagId"]) ||
    `auto-tag-${index}-${dimension}-${tagValue}`;

  return [
    {
      id,
      dimension: dimension || "–",
      tagValue: tagValue || "–",
      triggeredBy:
        pickString(item, [
          "triggered_by",
          "triggeredBy",
          "origin",
          "source",
          "created_by",
          "createdBy",
        ]) || "System",
      analysis: displayText(pickString(item, ["analysis", "rationale", "reason"])),
      summary: displayText(pickString(item, ["summary", "brief"])),
      recommendation: displayText(
        pickString(item, ["recommendation", "suggest", "suggestion"]),
      ),
      impactLabel: impact.impactLabel,
      impactBg: impact.impactBg,
      impactColor: impact.impactColor,
      hasImpact: impact.hasImpact,
      submissions: [],
    },
  ];
}

export function mapAutoTagDetailToImpactRows(
  payload: unknown,
): ImpactAssessmentRow[] {
  return extractDetailItems(payload).flatMap((item, index) =>
    expandAutoTagItem(item, index),
  );
}

function expandConditionalTagItem(
  raw: unknown,
  index: number,
): EnhanceTaggingRow[] {
  const item = asRecord(raw);
  if (!item) return [];

  const nestedValues = item.values ?? item.tags ?? item.concept_values;
  if (Array.isArray(nestedValues) && nestedValues.length > 0) {
    const dimension = pickConceptDimensionLabel(item);
    const parentDisplayName = pickString(item, [
      "concept_display_name",
      "conceptDisplayName",
    ]);

    return nestedValues.flatMap((value, valueIndex) => {
      const valueRec = asRecord(value);
      if (!valueRec) return [];
      const merged: Record<string, unknown> = {
        ...valueRec,
        concept_display_name:
          pickString(valueRec, [
            "concept_display_name",
            "conceptDisplayName",
          ]) ||
          parentDisplayName ||
          undefined,
        concept_name:
          pickString(valueRec, ["concept_name", "conceptName"]) ||
          pickString(item, ["concept_name", "conceptName"]) ||
          dimension,
      };
      merged.dimension = pickConceptDimensionLabel(merged, dimension);
      return expandConditionalTagItem(merged, index * 1000 + valueIndex);
    });
  }

  const dimension = pickConceptDimensionLabel(item);

  const tagValue =
    pickString(item, [
      "tag_value",
      "tagValue",
      "concept_value_name",
      "conceptValueName",
      "concept_value",
      "conceptValue",
      "name",
      "value",
      "label",
    ]) || pickNestedName(item.concept_value ?? item.conceptValue);

  const ruleId = pickString(item, [
    "rule_id",
    "ruleId",
    "conditional_plan_id",
    "conditionalPlanId",
    "plan_id",
    "planId",
    "id",
  ]);

  const rawCondition = pickRawCondition(item);
  const matchingCondition = resolveMatchingConditionDisplay(rawCondition);

  if (
    !dimension &&
    !tagValue &&
    !ruleId &&
    matchingCondition === "–" &&
    rawCondition == null
  ) {
    return [];
  }

  return [
    {
      id:
        pickString(item, ["id", "uuid"]) ||
        `conditional-tag-${index}-${dimension}-${tagValue}-${ruleId}`,
      dimension: dimension || "–",
      tagValue: tagValue || "–",
      ruleId: ruleId || "–",
      matchingCondition,
    },
  ];
}

export function mapConditionalTagDetailToEnhanceRows(
  payload: unknown,
): EnhanceTaggingRow[] {
  return extractDetailItems(payload).flatMap((item, index) =>
    expandConditionalTagItem(item, index),
  );
}

/**
 * GET /regulations/{id}/{version}/auto-tags/detail
 */
export async function getRegulationAutoTagDetail(
  regulationId: string,
  version: string,
): Promise<ApiResponse<ImpactAssessmentRow[] | null>> {
  const normalizedVersion = normalizeVersion(version);
  const result = await apiClient.get<unknown>(
    `/regulations/${encodeURIComponent(regulationId)}/${encodeURIComponent(normalizedVersion)}/auto-tags/detail`,
  );
  return {
    status: (result.status as "success" | "error") || "success",
    code: result.code,
    message: result.message ?? null,
    // null = not evaluated yet; [] / empty structure = evaluated with no tags
    data:
      result.data == null ? null : mapAutoTagDetailToImpactRows(result.data),
  };
}

/**
 * GET /regulations/{id}/{version}/conditional-tags/detail
 */
export async function getRegulationConditionalTagDetail(
  regulationId: string,
  version: string,
): Promise<ApiResponse<EnhanceTaggingRow[] | null>> {
  const normalizedVersion = normalizeVersion(version);
  const result = await apiClient.get<unknown>(
    `/regulations/${encodeURIComponent(regulationId)}/${encodeURIComponent(normalizedVersion)}/conditional-tags/detail`,
  );
  return {
    status: (result.status as "success" | "error") || "success",
    code: result.code,
    message: result.message ?? null,
    // null = not evaluated yet; [] / empty structure = evaluated with no tags
    data:
      result.data == null
        ? null
        : mapConditionalTagDetailToEnhanceRows(result.data),
  };
}
